import React from 'react';
import { useAppContext } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import FirmaDebug from './FirmaDebug';
import { LanguageSwitcher } from './LanguageSwitcher';
import StylesPanel from './StylesPanel';
import { Mail, Menu } from 'lucide-react';

interface HeaderProps {
  onMobileMenuToggle?: () => void;
  isMobile?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMobileMenuToggle }) => {
  const { selectedFirma } = useAppContext();
  const { profile } = useAuth();

  const firmaAdiGoster = selectedFirma ? (selectedFirma.label || selectedFirma.firma_adi) : 'Firma Seçilmedi';
  const firmaAdiTurkce = selectedFirma
    ? firmaAdiGoster.toLocaleUpperCase('tr-TR')
    : firmaAdiGoster;
  const rolEtiketi = profile?.rol
    ? profile.rol.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : null;

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
      <div className="h-1 w-full bg-[var(--panel-header-gradient)]" />

      <div className="flex h-[calc(var(--header-height)-4px)] items-center gap-3 px-3 sm:px-4 md:px-6">
        <div className="flex shrink-0 items-center gap-2">
          <button
            className="md:hidden flex items-center justify-center rounded-xl bg-[var(--panel-header-gradient)] shadow-md transition-all active:scale-95"
            style={{ width: 'clamp(40px, 9.6vw, 44px)', height: 'clamp(40px, 9.6vw, 44px)' }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onMobileMenuToggle?.();
            }}
            title="Menüyü aç"
            aria-label="Menüyü aç"
          >
            <Menu size={21} className="text-white" />
          </button>
        </div>

        <div className="min-w-0 flex-1 px-1 sm:px-2 text-center">
          <div className="flex min-w-0 items-center justify-center gap-2">
            <span
              className="truncate text-[clamp(1.02rem,1.9vw,1.48rem)] font-semibold leading-tight text-slate-900"
              title={selectedFirma ? firmaAdiTurkce : 'Firma seçilmedi'}
            >
              {selectedFirma ? firmaAdiTurkce : 'Çalışma alanınızı seçin'}
            </span>
          </div>

        </div>

        <div className="flex shrink-0 items-center gap-2 rounded-full border border-slate-200/80 bg-white/85 px-2 py-1 shadow-sm">
          <div className="flex items-center">
            <StylesPanel />
          </div>

          <div className="hidden sm:block">
            <a
              href="/admin/messages"
              className="flex items-center justify-center rounded-full bg-slate-50 p-2 transition-colors hover:bg-slate-100"
              title="Mesajlar"
              aria-label="Mesajlar"
            >
              <Mail size={18} className="text-slate-600" />
            </a>
          </div>

          <div className="flex items-center">
            <NotificationBell />
          </div>

          <div className="flex items-center">
            <LanguageSwitcher />
          </div>

          {selectedFirma?.logo_url && (
            <img
              src={selectedFirma.logo_url}
              alt="Firma Logosu"
              title={selectedFirma.label || selectedFirma.firma_adi || 'Firma logosu'}
              className="hidden h-9 max-w-[72px] object-contain sm:block"
            />
          )}

          {rolEtiketi && (
            <span
              className="hidden lg:inline-flex items-center rounded-full bg-[color-mix(in_srgb,var(--panel-header-bg)_14%,white)] px-3 py-1 text-[11px] font-semibold text-slate-700"
              title="Kullanıcı rolü"
            >
              {rolEtiketi}
            </span>
          )}
        </div>
      </div>

      {process.env.NODE_ENV === 'development' && <FirmaDebug />}
    </header>
  );
};

export default Header;
